//#region redux
import { AppState } from "store";
import { useSelector } from "react-redux";
//#endregion
//#region types
import { IconDefinitionConstants } from "utils/constants";
import { CommonEnums } from "models/enums";
import { MonthPickerQuickSelection } from "models/propTypes/datepickerPropTypes";
import { TranslationKeys } from "translations";
import { DateRange } from "models/formTypes/util";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";

function useAccountingDateRanges() {
  const financialYears = useSelector((store: AppState) => store.auth.selectedCompany.financialYears);

  const getMonthPickerQuickSelections = () => {
    const primaryColumnQuickSelections: MonthPickerQuickSelection[] = [];
    let comparisonColumnQuickSelections: MonthPickerQuickSelection[] = [];

    const now = new Date();
    const today = DateHelper.today();
    const validFinancialYears = financialYears?.filter((f) => new Date(f.start) < now);

    if (validFinancialYears?.length) {
      const financialYear = validFinancialYears[validFinancialYears.length - 1];
      primaryColumnQuickSelections.push({
        key: CommonEnums.AccountingDateTypes.FinancialYear,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.FinancialYear),
        range: { start: new Date(financialYear.start), end: new Date(financialYear.end) },
      });
    }

    primaryColumnQuickSelections.push(
      {
        key: CommonEnums.AccountingDateTypes.LastYear,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.Last12Months),
        range: { start: DateHelper.addMonths(today, -11), end: today },
      },
      {
        key: CommonEnums.AccountingDateTypes.CurrentMonth,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.CurrentMonth),
        range: { start: today, end: today },
      },
      {
        key: CommonEnums.AccountingDateTypes.LastQuarter,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.Last3Months),
        range: { start: DateHelper.addMonths(today, -2), end: today },
      }
    );

    comparisonColumnQuickSelections = [...primaryColumnQuickSelections];

    if (validFinancialYears?.length && validFinancialYears.length > 1) {
      const previousFinancialYear = validFinancialYears[validFinancialYears.length - 2];
      primaryColumnQuickSelections.push({
        key: CommonEnums.AccountingDateTypes.PreviousFinancialYear,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.PreviousFinancialYear),
        range: { start: new Date(previousFinancialYear.start), end: new Date(previousFinancialYear.end) },
      });
    }

    primaryColumnQuickSelections.push(
      {
        key: CommonEnums.AccountingDateTypes.Prior12Months,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.Previous12Months),
        range: { start: DateHelper.addMonths(today, -23), end: DateHelper.addMonths(today, -12) },
      },
      {
        key: CommonEnums.AccountingDateTypes.Prior1Month,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.PreviousMonth),
        range: { start: DateHelper.addMonths(today, -1), end: DateHelper.addMonths(today, -1) },
      },
      {
        key: CommonEnums.AccountingDateTypes.PreviousYearMonth,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.PreviousYearMonth),
        range: { start: DateHelper.addMonths(today, -12), end: DateHelper.addMonths(today, -12) },
      }
    );

    comparisonColumnQuickSelections.push(
      {
        key: CommonEnums.AccountingDateTypes.Prior12Months,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.Prior12Months),
        range: (primaryRange: DateRange) => ({
          start: primaryRange.start ? DateHelper.addMonths(primaryRange.start, -12) : undefined,
          end: primaryRange.end ? DateHelper.addMonths(primaryRange.end, -12) : undefined,
        }),
        icon: IconDefinitionConstants.WLink,
      },
      {
        key: CommonEnums.AccountingDateTypes.Prior1Month,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.Prior1Month),
        range: (primaryRange: DateRange) => ({
          start: primaryRange.start ? DateHelper.addMonths(primaryRange.start, -1) : undefined,
          end: primaryRange.end ? DateHelper.addMonths(primaryRange.end, -1) : undefined,
        }),
        icon: IconDefinitionConstants.WLink,
      },
      {
        key: CommonEnums.AccountingDateTypes.Prior3Months,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.Prior3Months),
        range: (primaryRange: DateRange) => ({
          start: primaryRange.start ? DateHelper.addMonths(primaryRange.start, -3) : undefined,
          end: primaryRange.end ? DateHelper.addMonths(primaryRange.end, -3) : undefined,
        }),
        icon: IconDefinitionConstants.WLink,
      },
      {
        key: CommonEnums.AccountingDateTypes.Prior24Months,
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.Prior24Months),
        range: (primaryRange: DateRange) => ({
          start: primaryRange.start ? DateHelper.addMonths(primaryRange.start, -24) : undefined,
          end: primaryRange.end ? DateHelper.addMonths(primaryRange.end, -24) : undefined,
        }),
        icon: IconDefinitionConstants.WLink,
      }
    );

    return { primaryColumnQuickSelections, comparisonColumnQuickSelections };
  };

  return getMonthPickerQuickSelections;
}

export default useAccountingDateRanges;
